import { useHeader } from "@highway/design-system";
import { Col, Row, useScreenSize } from "@highway/hwy-design-system";
import { TOrientation } from "@highway/mbs-remotion";
import { IBrandingOption } from "@highway/mbs-shared";
import { Stack, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";
import Header from "./Header/Header";
import QuoteSVG from "./QuoteSVG";
import { removeHashtags } from "../../utils/removeHashtags";

const LiveShareLayout = ({
    branding,
    caption,
    tagline,
    children,
    orientation = "landscape",
}: {
    branding: IBrandingOption | null;
    caption: string | null;
    tagline?: string;
    children: ReactNode;
    orientation?: TOrientation;
}) => {
    const theme = useTheme();
    const { isXS } = useScreenSize();
    const { header } = useHeader();

    return (
        <Row dynamicCols={[12, 8, 4]} sx={{ minHeight: "100vh" }}>
            <Col
                style={{
                    display: "flex",
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    background: branding
                        ? isXS
                            ? `linear-gradient(to bottom, ${branding.primary} 30%, ${theme.palette.primaryWhite.main} 30%)`
                            : `linear-gradient(to bottom, ${branding.primary} 55%, ${theme.palette.primaryWhite.main} 55%)`
                        : theme.palette.secondaryGreen.main,
                }}
                data-testid="marketing-layout"
            >
                <Stack
                    spacing={isXS ? "1rem" : "42px"}
                    alignItems="center"
                    style={{
                        width: "-webkit-fill-available",
                        maxWidth: orientation === "landscape" ? "700px" : "435px",
                        padding: "36px 16px",
                    }}
                >
                    {tagline && (
                        <Stack
                            spacing={1}
                            alignItems="center"
                            textAlign="center"
                            data-testid="marketing-layout-tagline"
                        >
                            <QuoteSVG
                                style={{ width: "25px", height: "20px" }}
                                color={branding ? branding.secondary : theme.palette.primaryBlack.main}
                            />
                            <Typography
                                variant="h3"
                                color={branding ? branding.secondary : theme.palette.primaryBlack.main}
                            >
                                {tagline}
                            </Typography>
                        </Stack>
                    )}
                    {children}
                    <Typography
                        variant="body2"
                        color={theme.palette.primaryBlack.main}
                        data-testid="marketing-layout-caption"
                    >
                        {removeHashtags(caption || "")}
                    </Typography>
                    {isXS && <Header header={header} />}
                </Stack>
            </Col>
            {!isXS && (
                <Col style={{ width: "360px", padding: "16px", boxSizing: "border-box" }}>
                    <Header header={header} />
                </Col>
            )}
        </Row>
    );
};

export default LiveShareLayout;
